// Client mirror of functions-common/src/repeat/locks.ts. Keep in sync.

import {
  daysForWeekKey,
  formatZonedDateKey,
  getWeekBucketKey,
  resolveWorkspaceTimeZone,
  weekKeyForDayKey,
} from './keys'
import { isDoneEveryPeriod, isDoneFor, isRepeatingItem } from './completions'
import { MAX_WINDOW_PERIODS, periodsInWindow } from './window'
import type {
  LockReason,
  LockStateResult,
  RepeatItemInput,
  RepeatPageDoc,
  RepeatPagesInput,
} from './types'

export const isChecklistGate = (
  item: Pick<RepeatItemInput, 'nodeKind'> | null | undefined,
): boolean => item?.nodeKind === 'GATE_AND' || item?.nodeKind === 'GATE_OR'

export function getItemFromGraph(
  graph:
    | ReadonlyMap<string, RepeatItemInput>
    | readonly RepeatItemInput[]
    | Record<string, RepeatItemInput>
    | null
    | undefined,
  id: string,
): RepeatItemInput | undefined {
  if (!graph) return undefined
  if (graph instanceof Map) {
    return graph.get(id)
  }
  if (Array.isArray(graph)) {
    return graph.find((candidate) => candidate.id === id)
  }
  return (graph as Record<string, RepeatItemInput>)[id]
}

const linkTitle = (item: RepeatItemInput | null | undefined, fallback?: string): string =>
  item?.title ?? fallback ?? 'Missing dependency'

function getGateBlockReasons(
  gate: RepeatItemInput,
  periodKey: string,
  graph:
    | ReadonlyMap<string, RepeatItemInput>
    | readonly RepeatItemInput[]
    | Record<string, RepeatItemInput>
    | null
    | undefined,
  pages?: RepeatPagesInput | null,
  now?: Date,
  timeZone?: string,
  todayIso?: string,
  itemIsRepeat?: boolean,
): LockReason[] {
  const inputIds = [
    ...new Set((gate.gateInputIds ?? []).filter((id) => typeof id === 'string' && id.length > 0)),
  ]
  if (inputIds.length < 2) {
    return [
      {
        code: 'DEPENDENCY_MISSING',
        targetId: gate.id,
        title: `${gate.title || 'Gate'} needs at least 2 inputs`,
      },
    ]
  }
  if (inputIds.length > 4) {
    return [
      {
        code: 'DEPENDENCY_MISSING',
        targetId: gate.id,
        title: `${gate.title || 'Gate'} has too many inputs`,
      },
    ]
  }

  const inputStates = inputIds.map((inputId) => {
    const input = getItemFromGraph(graph, inputId)
    const inBin = Boolean(input?.deletedAt != null)
    let resolved = false
    let missed: string[] | undefined
    if (input && !inBin) {
      if (isChecklistGate(input)) {
        const subReasons = getGateBlockReasons(
          input,
          periodKey,
          graph,
          pages,
          now,
          timeZone,
          todayIso,
          itemIsRepeat,
        )
        resolved = subReasons.length === 0
        if (!resolved) {
          missed = [...new Set(subReasons.flatMap((r) => r.missed ?? []))]
        }
      } else if (!itemIsRepeat && isRepeatingItem(input)) {
        const depTz = timeZone ?? 'UTC'
        const depToday = todayIso ?? formatZonedDateKey(now ?? new Date(), depTz)
        const depTodayKey =
          input.checklistType === 'WEEKLY'
            ? getWeekBucketKey(now ?? new Date(), depTz)
            : depToday
        if (isNotRequiredFromToday(input, depTodayKey, pages)) {
          resolved = true
        } else {
          const window = input.recurringWindow
          const windowEndIso = window?.endDate ? window.endDate.slice(0, 10) : null
          const windowEnded = Boolean(windowEndIso && windowEndIso < depToday)
          const checkUntil = windowEnded && windowEndIso ? windowEndIso : depToday
          const doneCheck = isDoneEveryPeriod(input, window, pages, checkUntil, depTz)
          resolved = Boolean(windowEnded && doneCheck.done)
          if (!resolved && doneCheck.missed.length > 0) {
            missed = doneCheck.missed
          }
        }
      } else {
        resolved = isDoneFor(input, periodKey, pages)
      }
    }
    return {
      inputId,
      input,
      inBin,
      resolved,
      missed,
    }
  })

  if (gate.nodeKind === 'GATE_OR') {
    if (inputStates.some((state) => state.resolved)) return []
    return inputStates.map((state) => ({
      code: state.input && !state.inBin ? 'DEPENDENCY_INCOMPLETE' : 'DEPENDENCY_MISSING',
      targetId: state.inputId,
      title: linkTitle(state.input, state.inputId),
      ...(state.missed && state.missed.length > 0 ? { missed: state.missed } : {}),
      ...(state.inBin ? { inBin: true } : {}),
    }))
  }

  return inputStates.flatMap((state) => {
    if (state.resolved) return []
    return [
      {
        code: state.input && !state.inBin ? 'DEPENDENCY_INCOMPLETE' : 'DEPENDENCY_MISSING',
        targetId: state.inputId,
        title: linkTitle(state.input, state.inputId),
        ...(state.missed && state.missed.length > 0 ? { missed: state.missed } : {}),
        ...(state.inBin ? { inBin: true } : {}),
      },
    ]
  })
}

/**
 * Seam for W3 "not required from today on".
 * Future home: CR3.a, row W3 (docs/plans/checklist-canvas-reliability.md).
 */
export function isNotRequiredFromToday(
  item: RepeatItemInput,
  todayKey: string,
  pages?: RepeatPagesInput | null,
): boolean {
  if (
    typeof item.notRequiredFrom === 'string' &&
    item.notRequiredFrom.length > 0 &&
    item.notRequiredFrom <= todayKey
  ) {
    return true
  }
  if (!isRepeatingItem(item) && item.workflowStatus === 'NOT_REQUIRED') {
    return true
  }
  if (!pages) return false

  const allPages: RepeatPageDoc[] = Array.isArray(pages)
    ? (pages as RepeatPageDoc[])
    : pages instanceof Map
      ? Array.from(pages.values())
      : Object.values(pages)

  const todayPage = allPages.find((p) => p.pageKey === todayKey)
  if (!todayPage?.completions) return false
  const todayCompletion = todayPage.completions[item.id]
  if (todayCompletion?.workflowStatus !== 'NOT_REQUIRED') {
    return false
  }

  const laterPages = allPages.filter((p) => p.pageKey > todayKey)
  for (const page of laterPages) {
    const comp = page.completions?.[item.id]
    if (comp?.workflowStatus !== 'NOT_REQUIRED') {
      return false
    }
  }

  return true
}

export function lockStateFor(
  item: RepeatItemInput,
  periodKey: string,
  graph:
    | ReadonlyMap<string, RepeatItemInput>
    | readonly RepeatItemInput[]
    | Record<string, RepeatItemInput>
    | null
    | undefined,
  pages?: RepeatPagesInput | null,
  now: Date | number | string = new Date(),
  timeZone?: string,
): LockStateResult {
  const reasons: LockReason[] = []
  const seenDependencyIds = new Set<string>()
  const dependencyIds = [
    ...(item.dependencyNodeIds ?? []),
    ...(item.prerequisites ?? []),
    ...(item.blockedByNodeIds ?? []).map((link) => link.targetId),
  ].filter((id): id is string => typeof id === 'string' && id.length > 0)

  const itemIsRepeat = isRepeatingItem(item)
  const tz = resolveWorkspaceTimeZone(timeZone)
  const nowDate = now instanceof Date ? now : new Date(now)
  const todayIso = formatZonedDateKey(nowDate, tz)

  for (const dependencyId of dependencyIds) {
    if (seenDependencyIds.has(dependencyId)) continue
    seenDependencyIds.add(dependencyId)

    const dependency = getItemFromGraph(graph, dependencyId)
    if (!dependency) {
      reasons.push({ code: 'DEPENDENCY_MISSING', targetId: dependencyId })
      continue
    }

    if (dependency.deletedAt != null) {
      reasons.push({
        code: 'DEPENDENCY_MISSING',
        targetId: dependencyId,
        title: linkTitle(dependency),
        inBin: true,
      })
      continue
    }

    if (isChecklistGate(dependency)) {
      reasons.push(
        ...getGateBlockReasons(
          dependency,
          periodKey,
          graph,
          pages,
          nowDate,
          tz,
          todayIso,
          itemIsRepeat,
        ),
      )
      continue
    }

    const depIsRepeat = isRepeatingItem(dependency)

    // L1: One-off waiting on one-off
    if (!itemIsRepeat && !depIsRepeat) {
      if (!isDoneFor(dependency, periodKey, pages)) {
        reasons.push({
          code: 'DEPENDENCY_INCOMPLETE',
          targetId: dependencyId,
          title: linkTitle(dependency),
        })
      }
      continue
    }

    // Repeating waiting on one-off (D/W <- I): unlocked on every period once one-off is done
    if (itemIsRepeat && !depIsRepeat) {
      if (!isDoneFor(dependency, periodKey, pages)) {
        reasons.push({
          code: 'DEPENDENCY_INCOMPLETE',
          targetId: dependencyId,
          title: linkTitle(dependency),
        })
      }
      continue
    }

    // L2 / L3: Repeating waiting on repeating (D <- D, W <- W, D <- W, W <- D)
    if (itemIsRepeat && depIsRepeat) {
      if (item.checklistType === 'WEEKLY' && dependency.checklistType === 'DAILY') {
        const { monday: weekMondayIso, sunday: weekSundayIso } = daysForWeekKey(periodKey)
        const depWindow = dependency.recurringWindow
        const depStart = depWindow?.startDate ? depWindow.startDate.slice(0, 10) : weekMondayIso
        const depEnd = depWindow?.endDate ? depWindow.endDate.slice(0, 10) : weekSundayIso

        const weekStart = depStart > weekMondayIso ? depStart : weekMondayIso
        const weekEnd = depEnd < weekSundayIso ? depEnd : weekSundayIso
        const checkUntil = todayIso < weekSundayIso ? todayIso : weekSundayIso

        const weekWindow = { startDate: weekStart, endDate: weekEnd }
        const doneCheck = isDoneEveryPeriod(dependency, weekWindow, pages, checkUntil, tz)

        if (!doneCheck.done) {
          const reason: LockReason = {
            code: 'DEPENDENCY_INCOMPLETE',
            targetId: dependencyId,
            title: linkTitle(dependency),
          }
          if (doneCheck.missed.length > 0) {
            reason.missed = doneCheck.missed
          }
          reasons.push(reason)
        }
        continue
      }

      let depPeriod = periodKey
      if (item.checklistType === 'DAILY' && dependency.checklistType === 'WEEKLY') {
        depPeriod = weekKeyForDayKey(periodKey)
      }
      if (!isDoneFor(dependency, depPeriod, pages)) {
        reasons.push({
          code: 'DEPENDENCY_INCOMPLETE',
          targetId: dependencyId,
          title: linkTitle(dependency),
        })
      }
      continue
    }

    // L4: One-off waiting on repeating (I <- D/W):
    // Unlocked only when every period so far is done AND the window has ended,
    // or the item is marked not required from today on (CR-D2, CR-P5).
    if (!itemIsRepeat && depIsRepeat) {
      const depTodayKey =
        dependency.checklistType === 'WEEKLY'
          ? getWeekBucketKey(nowDate, tz)
          : todayIso
      if (isNotRequiredFromToday(dependency, depTodayKey, pages)) {
        // Marked not required from today on: unlocked!
        continue
      }

      const window = dependency.recurringWindow
      const windowEndIso = window?.endDate ? window.endDate.slice(0, 10) : null
      const windowEnded = Boolean(windowEndIso && windowEndIso < todayIso)

      const checkUntil = windowEnded && windowEndIso ? windowEndIso : todayIso
      const doneCheck = isDoneEveryPeriod(dependency, window, pages, checkUntil, tz)

      if (!windowEnded || !doneCheck.done) {
        const reason: LockReason = {
          code: 'DEPENDENCY_INCOMPLETE',
          targetId: dependencyId,
          title: linkTitle(dependency),
        }
        if (doneCheck.missed.length > 0) {
          reason.missed = doneCheck.missed
        }
        reasons.push(reason)
      }
      continue
    }
  }

  // Pass-through reasons: copy exact order from checklistDependencyState.ts:99-110
  if (item.requiresProofFile === true && !item.proofFileUrl && !item.proofSubmissionId) {
    reasons.push({ code: 'PROOF_REQUIRED' })
  }
  if (item.requiresLocationPin === true && !item.verifiedCoordinates) {
    reasons.push({ code: 'LOCATION_REQUIRED' })
  }
  if ((item as RepeatItemInput & { resourceGateStatus?: string }).resourceGateStatus === 'WARNING') {
    reasons.push({ code: 'RESOURCE_WARNING' })
  }
  if (item.blockedByExternal) {
    reasons.push({ code: 'EXTERNAL_BLOCKER', targetId: item.blockedByExternal.targetId })
  }

  return {
    isBlocked: reasons.length > 0,
    reasons,
    blockerCount: reasons.filter(
      (reason) => reason.code === 'DEPENDENCY_INCOMPLETE' || reason.code === 'DEPENDENCY_MISSING',
    ).length,
  }
}

export function pageKeysForLock(
  item: RepeatItemInput,
  periodKey: string,
  graph:
    | ReadonlyMap<string, RepeatItemInput>
    | readonly RepeatItemInput[]
    | Record<string, RepeatItemInput>
    | null
    | undefined,
  now: Date | number | string = new Date(),
  timeZone?: string,
): Array<{ checklistType: 'DAILY' | 'WEEKLY'; pageKey: string }> {
  const result: Array<{ checklistType: 'DAILY' | 'WEEKLY'; pageKey: string }> = []
  const seenPageKeys = new Set<string>()
  const addPageKey = (checklistType: 'DAILY' | 'WEEKLY', pageKey: string) => {
    const key = `${checklistType}:${pageKey}`
    if (!seenPageKeys.has(key)) {
      seenPageKeys.add(key)
      result.push({ checklistType, pageKey })
    }
  }

  const tz = resolveWorkspaceTimeZone(timeZone)
  const nowDate = now instanceof Date ? now : new Date(now)
  const todayIso = formatZonedDateKey(nowDate, tz)
  const itemIsRepeat = isRepeatingItem(item)

  const visitedNodeIds = new Set<string>()
  const pendingDepIds: string[] = []

  const enqueueDeps = (target: RepeatItemInput) => {
    const depIds = isChecklistGate(target)
      ? target.gateInputIds ?? []
      : [
          ...(target.dependencyNodeIds ?? []),
          ...(target.prerequisites ?? []),
          ...(target.blockedByNodeIds ?? []).map((link) => link.targetId),
        ]
    for (const id of depIds) {
      if (typeof id === 'string' && id.length > 0 && !visitedNodeIds.has(id)) {
        visitedNodeIds.add(id)
        pendingDepIds.push(id)
      }
    }
  }

  enqueueDeps(item)

  while (pendingDepIds.length > 0) {
    const depId = pendingDepIds.shift()!
    const dep = getItemFromGraph(graph, depId)
    if (!dep || dep.deletedAt != null) continue

    if (isChecklistGate(dep)) {
      enqueueDeps(dep)
      continue
    }

    const depIsRepeat = isRepeatingItem(dep)
    if (!depIsRepeat) {
      continue
    }

    const depType = dep.checklistType as 'DAILY' | 'WEEKLY'

    if (itemIsRepeat) {
      if (item.checklistType === depType) {
        addPageKey(depType, periodKey)
      } else if (item.checklistType === 'DAILY' && depType === 'WEEKLY') {
        addPageKey('WEEKLY', weekKeyForDayKey(periodKey))
      } else if (item.checklistType === 'WEEKLY' && depType === 'DAILY') {
        const { monday: weekMondayIso, sunday: weekSundayIso } = daysForWeekKey(periodKey)
        const depWindow = dep.recurringWindow
        const depStart = depWindow?.startDate ? depWindow.startDate.slice(0, 10) : weekMondayIso
        const depEnd = depWindow?.endDate ? depWindow.endDate.slice(0, 10) : weekSundayIso

        const weekStart = depStart > weekMondayIso ? depStart : weekMondayIso
        const weekEnd = depEnd < weekSundayIso ? depEnd : weekSundayIso
        const checkUntil = todayIso < weekSundayIso ? todayIso : weekSundayIso

        const weekWindow = { startDate: weekStart, endDate: weekEnd }
        const days = periodsInWindow('DAILY', weekWindow, tz, checkUntil)
        for (const day of days) {
          addPageKey('DAILY', day)
        }
      }
    } else {
      const periods = periodsInWindow(depType, dep.recurringWindow, tz, todayIso, MAX_WINDOW_PERIODS)
      for (const p of periods) {
        addPageKey(depType, p)
      }
    }
  }

  return result
}
